import { CloseButton, Image, Popover, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { RefreshCw } from "lucide-react";
import { useState } from "react";

import classes from "./forms.module.css";

/** One of the product's pictures the palette can read its colors from. */
export interface PalettePicture {
  assetId: string;
  /** The slot it is in, from 0, for its name. */
  slot: number;
  url: string | null;
}

export interface ReextractColorsProps {
  pictures: PalettePicture[];
  /** The colors are being read: the icon turns and the tile takes no click. */
  running: boolean;
  /** An upload runs, or the form is busy: the tile takes no click. */
  disabled: boolean;
  onPick: (picture: PalettePicture) => void;
}

/**
 * Live's "Re-extract from Image" in the product palette (spec G15c, capture
 * `design-ref/brands-1024`): a dashed tile after the colors, shown once a slot holds a picture,
 * that opens "Select Image for Color Extraction" with the product's pictures in slot order; a
 * picked one closes it and its colors become the product's.
 */
export function ReextractColors({ pictures, running, disabled, onPick }: ReextractColorsProps) {
  const [opened, setOpened] = useState(false);
  if (pictures.length === 0) return null;
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withArrow
      shadow="md"
    >
      <Popover.Target>
        <Tooltip label="Re-extract from Image" withArrow disabled={opened || running}>
          <UnstyledButton
            className={`${classes.zoneAdd} ${classes.colorAdd}`}
            aria-label="Re-extract from Image"
            aria-busy={running || undefined}
            disabled={disabled || running}
            onClick={() => setOpened((open) => !open)}
          >
            <RefreshCw
              size={20}
              aria-hidden
              className={running ? classes.reextractTurning : undefined}
            />
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown className={classes.reextract}>
        <div className={classes.reextractHeader}>
          <Text className={classes.reextractTitle}>Select Image for Color Extraction</Text>
          <CloseButton size="sm" aria-label="Close" onClick={() => setOpened(false)} />
        </div>
        <div className={classes.reextractImages}>
          {pictures.map((picture) => (
            <UnstyledButton
              key={picture.assetId}
              className={classes.reextractImage}
              aria-label={`Extract the colors of image ${picture.slot + 1}`}
              onClick={() => {
                setOpened(false);
                onPick(picture);
              }}
            >
              {picture.url === null ? null : <Image src={picture.url} alt="" />}
            </UnstyledButton>
          ))}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
